import { action, computed, observable } from "mobx";
import TourStore from "~/stores/TourStore";
import Immersive3dTourFloorPlanImportRepository from "~/repository/Immersive3dTour/Immersive3dTourFloorPlanImportRepository";
import TourInboxImageStore from "~/stores/TourInboxImageStore";
import Immersive3dTourFloorPlanImportStatus from "~/constants/Immersive3dTour/Immersive3dTourFloorPlanImportStatus";
import FlashMessageStore from "~/stores/FlashMessageStore";
import TourJobFileStore from "~/stores/TourJob/TourJobFileStore";
import Immersive3dTourFloorPlanImportStore from "./Immersive3dTourFloorPlanImportStore";
import Immersive3dTourFloorPlanUpdateRequestStore from "./Immersive3dTourFloorPlanUpdateRequestStore";

class Immersive3dTourFloorPlanImportModalStore {
  /**
   * @property {Immersive3dTourFloorPlanImportStore}
   */
  immersive3dTourFloorPlanImportStore;

  /**
   * @property {Immersive3dTourFloorPlanUpdateRequestStore}
   */
  immersive3dTourFloorPlanUpdateRequestStore;

  /**
   * @property {TourJobFileStore}
   */
  tourJobFileStore;

  /**
   * @property {TourInboxImageStore}
   */
  tourInboxImageStore;

  /**
   * @property {TourStore}
   */
  tourStore;

  flashMessageStore = null;

  /**
   * @property {Array}
   */
  @observable immersive3dTourFloorPlans = [];

  tourId = null;

  immersive3dTourFloorPlanImportRepository = null;

  constructor() {
    this.immersive3dTourFloorPlanImportStore = new Immersive3dTourFloorPlanImportStore();
    this.immersive3dTourFloorPlanUpdateRequestStore = new Immersive3dTourFloorPlanUpdateRequestStore();
    this.tourJobFileStore = new TourJobFileStore();
    this.tourInboxImageStore = new TourInboxImageStore();
    this.tourStore = new TourStore();
    this.flashMessageStore = new FlashMessageStore();
    this.flashMessageStore.timeout = 10000;

    this.immersive3dTourFloorPlanImportRepository = new Immersive3dTourFloorPlanImportRepository();
  }

  @computed
  get isLoading() {
    return (
      this.immersive3dTourFloorPlanImportStore.isLoading ||
      this.immersive3dTourFloorPlanUpdateRequestStore.isLoading ||
      this.tourJobFileStore.isLoading ||
      this.tourInboxImageStore.isLoading ||
      this.tourStore.isLoading
    );
  }

  @action
  toggleSelected(immersive3dTourFloorPlanId) {
    const index = this.immersive3dTourFloorPlans.findIndex(floorPlan => floorPlan.id === immersive3dTourFloorPlanId);

    if (index === -1 || this.immersive3dTourFloorPlans[index].disabled) {
      return;
    }

    this.immersive3dTourFloorPlans[index].checked = !this.immersive3dTourFloorPlans[index].checked;
  }

  @action
  load() {
    this.tourJobFileStore.isLoading = true;
    this.tourInboxImageStore.isLoading = true;
    this.tourStore.isLoading = true;

    this.tourJobFileStore.searchParams.tourId = this.tourId;
    this.tourJobFileStore.pagination.itemsPerPage = 100;
    this.tourInboxImageStore.tourId = this.tourId;
    this.tourStore.tourId = this.tourId;

    return Promise.all([
      this.tourJobFileStore.loadIndex(),
      this.tourInboxImageStore.loadIndex(),
      this.tourStore.getOne(),
    ]).then(() => {
      const { tourJobFiles } = this.tourJobFileStore;

      this.immersive3dTourFloorPlans = [];

      tourJobFiles.forEach(tourJobFile => {
        if (tourJobFile.mimeType !== "image/jpeg") {
          return;
        }

        const originalFileName = this.getOriginalFileName(tourJobFile.fileUrl);

        const immersive3dTourFloorPlan = {
          id: tourJobFile.tourJobFileId,
          name: tourJobFile.name,
          originalFilename: originalFileName,
          mimeType: tourJobFile.mimeType,
          fileUrl: tourJobFile.fileUrl,
          checked: false,
          status: Immersive3dTourFloorPlanImportStatus.NEW,
          files: [{ mimeType: "image/jpeg", tourJobFileId: tourJobFile.tourJobFileId }],
        };

        if (this.tourInboxImageOriginalFilenames.includes(originalFileName)) {
          immersive3dTourFloorPlan.status = Immersive3dTourFloorPlanImportStatus.QUEUED;
        } else if (this.floorPlanOriginalFilenames.includes(originalFileName)) {
          immersive3dTourFloorPlan.status = Immersive3dTourFloorPlanImportStatus.DONE;
        }

        this.immersive3dTourFloorPlans.push(immersive3dTourFloorPlan);
      });

      // Let's add the rest of the files
      tourJobFiles.forEach(tourJobFile => {
        if (tourJobFile.mimeType === "image/jpeg") {
          return;
        }

        // We will identify the connection based on the original file name
        const originalBaseFileName = this.getOriginalBaseFileName(tourJobFile.fileUrl);

        const immersive3dTourFloorPlan = this.immersive3dTourFloorPlans.find(({ originalFilename }) =>
          originalFilename.includes(originalBaseFileName),
        );

        immersive3dTourFloorPlan.files.push({
          mimeType: tourJobFile.mimeType,
          tourJobFileId: tourJobFile.tourJobFileId,
        });
      });

      this.tourJobFileStore.isLoading = false;
      this.tourInboxImageStore.isLoading = false;
      this.tourStore.isLoading = false;
    });
  }

  @action
  importSelectedFloorPlans() {
    this.immersive3dTourFloorPlanImportStore.tourId = this.tourId;

    const images = [];
    this.immersive3dTourFloorPlans.forEach(floorPlan => {
      if (floorPlan.checked) {
        images.push({
          name: floorPlan.name,
          src: floorPlan.fileUrl,
          originalFilename: floorPlan.originalFilename,
        });
      }
    });

    return this.immersive3dTourFloorPlanImportStore
      .update(images)
      .then(() => {
        this.load();
      })
      .catch(error => {
        this.flashMessageStore.setMessage(error.message, "negative");
      });
  }

  @computed
  get tourInboxImageOriginalFilenames() {
    const { tourInboxImages } = this.tourInboxImageStore;

    const tourInboxImageOriginalFilenames = [];

    for (let i = 0; i < tourInboxImages.length; i += 1) {
      tourInboxImageOriginalFilenames.push(tourInboxImages[i].originalFileName);
    }

    return tourInboxImageOriginalFilenames;
  }

  @computed
  get floorPlanOriginalFilenames() {
    const { tour } = this.tourStore;

    const floorPlanOriginalFilenames = [];

    if (tour && tour.photos) {
      for (let i = 0; i < tour.photos.length; i += 1) {
        if (tour.photos[i].type === "Floorplan") {
          floorPlanOriginalFilenames.push(tour.photos[i].originalFilename);
        }
      }
    }

    return floorPlanOriginalFilenames;
  }

  @computed
  get newCheckedImmersive3dTourFloorPlans() {
    return this.immersive3dTourFloorPlans.filter(immersive3dTourFloorPlan => immersive3dTourFloorPlan.checked);
  }

  getOriginalFileName(str) {
    return String(str).substring(str.lastIndexOf("/") + 1);
  }

  getOriginalBaseFileName(str) {
    let base = this.getOriginalFileName(str);

    if (base.lastIndexOf(".") !== -1) {
      base = base.substring(0, base.lastIndexOf("."));
    }

    return base;
  }

  @action
  requestFloorPlanUpdate() {
    this.immersive3dTourFloorPlanUpdateRequestStore.tourId = this.tourId;

    return this.immersive3dTourFloorPlanUpdateRequestStore.update().catch(error => {
      this.flashMessageStore.setMessage(error.message, "negative");

      throw error;
    });
  }

  @computed
  get lastPhotoUploadedDate() {
    const { tour } = this.tourStore;

    let lastPhotoUploadedDate = null;

    if (tour && tour.photos) {
      for (let i = 0; i < tour.photos.length; i += 1) {
        if (tour.photos[i].type === "Floorplan") {
          if (tour.photos[i].uploadDts !== null && tour.photos[i].uploadDts > lastPhotoUploadedDate) {
            lastPhotoUploadedDate = tour.photos[i].uploadDts;
          }
        }
      }
    }

    return lastPhotoUploadedDate;
  }
}

export default Immersive3dTourFloorPlanImportModalStore;
